import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  FolderOpen, 
  Lightbulb, 
  ArrowRight,
  Trash, 
  ArrowUpCircle,
  Calendar,
  User,
  FileCheck,
  Package
} from "lucide-react";
import { getRecommendationTypeIcon, formatCurrency, formatDate } from "@/lib/utils";
import { OptimizationRecommendation, Application, License } from "@shared/schema";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface RecommendationDetailDialogProps {
  recommendationId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AffectedItems {
  type: string;
  items: any[];
}

export default function RecommendationDetailDialog({
  recommendationId,
  open,
  onOpenChange
}: RecommendationDetailDialogProps) {
  const [, navigate] = useLocation();

  // Fetch recommendation data
  const { data: recommendation, isLoading: recLoading } = useQuery<OptimizationRecommendation>({
    queryKey: ['/api/recommendations', recommendationId],
    queryFn: async () => {
      if (!recommendationId) throw new Error("Recommendation ID is required");
      const res = await fetch(`/api/recommendations/${recommendationId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch recommendation');
      }
      return res.json();
    },
    enabled: !!recommendationId && open,
  });

  // Fetch application data if applicable
  const { data: application, isLoading: appLoading } = useQuery<Application>({
    queryKey: ['/api/applications', recommendation?.applicationId],
    queryFn: async () => {
      if (!recommendation?.applicationId) throw new Error("Application ID is required");
      const res = await fetch(`/api/applications/${recommendation.applicationId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch application');
      }
      return res.json();
    },
    enabled: !!recommendation?.applicationId && open,
  });
  
  // Fetch affected items
  const { data: affectedItems, isLoading: itemsLoading } = useQuery<AffectedItems>({
    queryKey: ['/api/recommendations', recommendationId, 'affected-items'],
    queryFn: async () => {
      if (!recommendationId) throw new Error("Recommendation ID is required");
      const res = await fetch(`/api/recommendations/${recommendationId}/affected-items`);
      if (!res.ok) {
        throw new Error('Failed to fetch affected items');
      }
      return res.json();
    },
    enabled: !!recommendationId && open,
  });

  const isLoading = recLoading || appLoading || itemsLoading;

  // Function to get the appropriate Lucide icon component
  const getIconComponent = (name: string) => {
    switch (name) {
      case 'DollarSign': return <DollarSign className="h-5 w-5" />;
      case 'FolderOpen': return <FolderOpen className="h-5 w-5" />;
      case 'Trash': return <Trash className="h-5 w-5" />;
      case 'ArrowUpCircle': return <ArrowUpCircle className="h-5 w-5" />;
      default: return <Lightbulb className="h-5 w-5" />;
    }
  };

  // Generate a relevant CTA link based on recommendation type
  const getActionLink = () => {
    if (!recommendation) return '/licenses';
    
    const title = recommendation.title.toLowerCase();
    // Applications page has been removed, redirect all application-related items to licenses
    if (title.includes('license') || title.includes('storage') || title.includes('duplicate') || title.includes('application')) {
      return '/licenses';
    } else {
      return '/cost-management';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <>
            <DialogHeader>
              <Skeleton className="h-8 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full" />
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-36 w-full" />
            </div>
          </>
        ) : recommendation ? (
          <>
            <DialogHeader>
              <div className="flex items-center">
                {(() => {
                  const { iconName, color } = getRecommendationTypeIcon(recommendation.title);
                  return (
                    <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center mr-4`}>
                      {getIconComponent(iconName)}
                    </div>
                  );
                })()}
                <DialogTitle className="text-xl">{recommendation.title}</DialogTitle>
              </div>
              <DialogDescription>
                {recommendation.description}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-1">Potential Monthly Savings</h4>
                <p className="text-2xl font-semibold text-green-600">{formatCurrency(recommendation.potentialSavings)}</p>
                <p className="text-sm text-slate-500">Per month</p>
                <p className="text-sm font-medium text-green-600 mt-2">
                  {formatCurrency(recommendation.potentialSavings * 12)} annual savings
                </p>
              </div>

              <div className="border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-1">Affected Application</h4>
                {application ? (
                  <div className="flex items-center">
                    <div className="mr-2 w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                      {application.logo ? 
                        getIconComponent(getRecommendationTypeIcon(application.logo).iconName) : 
                        <i className="ri-computer-line"></i>
                      }
                    </div>
                    <div>
                      <p className="font-medium">{application.name}</p>
                      <p className="text-xs text-slate-500">{application.vendor}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-600">Multiple applications</p>
                )}
                <p className="text-xs text-slate-500 mt-2">Identified on {formatDate(recommendation.createdAt)}</p>
              </div>
            </div>

            {/* Affected Items Section */}
            {affectedItems && affectedItems.items.length > 0 && (
              <div className="mb-6 border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-4">
                  {affectedItems.type === 'licenses' ? 'Affected Licenses' : 
                   affectedItems.type === 'duplicate-applications' ? 'Duplicate Applications' : 
                   'Affected Items'}
                </h4>
                
                {affectedItems.type === 'licenses' && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>License</TableHead>
                        <TableHead>Total Seats</TableHead>
                        <TableHead>Assigned</TableHead>
                        <TableHead>Utilization</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affectedItems.items.map((license: License) => (
                        <TableRow key={license.id}>
                          <TableCell className="font-medium">{license.name}</TableCell>
                          <TableCell>{license.totalSeats}</TableCell>
                          <TableCell>{license.assignedSeats}</TableCell>
                          <TableCell>
                            {Math.round((license.assignedSeats / license.totalSeats) * 100)}%
                          </TableCell>
                          <TableCell>
                            <Badge variant={license.status === 'Active' ? 'default' : 'secondary'}>
                              {license.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                
                {affectedItems.type === 'duplicate-applications' && (
                  <div className="space-y-4">
                    {affectedItems.items.map((vendorGroup: any, index: number) => (
                      <div key={index} className="border border-slate-100 rounded p-3">
                        <h5 className="font-medium mb-2">Category: {vendorGroup.category}</h5>
                        {vendorGroup.duplicateGroups ? (
                          // New structure with duplicate groups
                          vendorGroup.duplicateGroups.map((nameGroup: any, nameIndex: number) => (
                            <div key={nameIndex} className="mb-3">
                              <div className="text-sm font-medium text-slate-600 mb-1 pl-1">
                                Group: {nameGroup.groupName}
                                {nameGroup.applications.length > 1 && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    Duplicate detected
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-1 gap-2">
                                {nameGroup.applications.map((app: Application) => (
                                  <div key={app.id} className="flex items-center bg-slate-50 p-2 rounded">
                                    <div className="mr-2 w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                                      {app.logo ? 
                                        getIconComponent(getRecommendationTypeIcon(app.logo).iconName) : 
                                        <Package className="h-4 w-4" />
                                      }
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-medium">{app.name}</p>
                                      <p className="text-xs text-slate-500">{app.vendor}</p>
                                    </div>
                                    {nameGroup.applications.length > 1 && (
                                      <div className="text-xs text-slate-500 italic">
                                        Potential duplicate
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))
                        ) : (
                          // Fallback for old format
                          <div className="grid grid-cols-1 gap-2">
                            {vendorGroup.applications && vendorGroup.applications.map((app: Application) => (
                              <div key={app.id} className="flex items-center bg-slate-50 p-2 rounded">
                                <div className="mr-2 w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                                  {app.logo ? 
                                    getIconComponent(getRecommendationTypeIcon(app.logo).iconName) : 
                                    <Package className="h-4 w-4" />
                                  }
                                </div>
                                <div>
                                  <p className="font-medium">{app.name}</p>
                                  <p className="text-xs text-slate-500">{app.vendor}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {affectedItems.type === 'application' && (
                  <div className="flex items-center">
                    {affectedItems.items.map((app: Application) => (
                      <div key={app.id} className="flex items-center">
                        <div className="mr-2 w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                          {app.logo ? 
                            getIconComponent(getRecommendationTypeIcon(app.logo).iconName) : 
                            <Package className="h-4 w-4" />
                          }
                        </div>
                        <div>
                          <p className="font-medium">{app.name}</p>
                          <p className="text-xs text-slate-500">{app.vendor}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="pt-4 border-t border-slate-200">
              <h4 className="text-sm font-medium mb-3">Implementation Guide</h4>
              
              <div className="space-y-4">
                {recommendation.title.toLowerCase().includes('license') && (
                  <>
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">1</span>
                      </div>
                      <p>Review the current license allocation and identify unused or underutilized licenses.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">2</span>
                      </div>
                      <p>Contact your account manager to discuss reducing the number of licenses.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">3</span>
                      </div>
                      <p>Update the license details in the system once the changes are confirmed.</p>
                    </div>
                  </>
                )}
                
                {recommendation.title.toLowerCase().includes('storage') && (
                  <>
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">1</span>
                      </div>
                      <p>Review all current storage solutions and their usage patterns.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">2</span>
                      </div>
                      <p>Create a consolidation plan for migrating data to the preferred storage solution.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">3</span>
                      </div>
                      <p>Cancel or downgrade unnecessary storage subscriptions after migration.</p>
                    </div>
                  </>
                )}
                
                {recommendation.title.toLowerCase().includes('duplicate') && (
                  <>
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">1</span>
                      </div>
                      <p>Identify the overlapping tools and select the preferred solution based on features and usage.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">2</span>
                      </div>
                      <p>Notify users about the plan to standardize on a single solution.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">3</span>
                      </div>
                      <p>Cancel subscriptions for the redundant solutions after the transition period.</p>
                    </div>
                  </>
                )}
                
                {!recommendation.title.toLowerCase().includes('license') && 
                 !recommendation.title.toLowerCase().includes('storage') && 
                 !recommendation.title.toLowerCase().includes('duplicate') && (
                  <>
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">1</span>
                      </div>
                      <p>Analyze the current usage patterns and identify optimization opportunities.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">2</span>
                      </div>
                      <p>Create an implementation plan with stakeholders.</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-slate-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-sm font-medium">3</span>
                      </div>
                      <p>Make necessary adjustments to optimize costs.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            <DialogFooter className="flex justify-between items-center">
              <div className="text-sm text-slate-500 italic">
                Potential savings are estimates based on current usage patterns
              </div>
              <Button onClick={() => {
                // If we have affected items, don't navigate away, just close the dialog
                if (affectedItems && affectedItems.items.length > 0) {
                  onOpenChange(false);
                } else {
                  // Otherwise use the default behavior - navigate to the relevant page
                  const url = getActionLink();
                  onOpenChange(false);
                  navigate(url);
                }
              }}>
                {affectedItems && affectedItems.items.length > 0 ? 'Close' : 'Take Action'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-8 text-center">
            <p>Unable to load recommendation details.</p>
            <Button variant="secondary" className="mt-4" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}